import React, { useEffect, useMemo, useRef, useState } from "react";
import { Select, Modal, Table as AntTable } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import Table from "@/components/Table";
import Button from "@components/Button";
import CompanyList from "@/components/List/company-list";
import { ICompany } from "@/types";

import {
  useFetchAllJobFunctionsQuery,
  useFetchAllCompanyFunctionsMappingQuery,
  useFetchCompaniesQuery,
} from "@/services";

import "./companyFunctionsMapTable.less";

const { Option } = Select;

type RowData = {
  id: string;
  companyJobFunctionName: string;
  companySubJobFunctionName: string;
  jobFunctionId?: string;
  subJobFunctionId?: string;
};

const PAGE_SIZE = 5;

const CompanyFunctionsMapTable: React.FC = () => {
  const [selectedCompany, setSelectedCompany] = useState<string | null>(null);
  const [companyId, setCompanyId] = useState<number | null>(null);

  // ---------------- COMPANY LIST ----------------

  const { data: companiesData, isLoading: isFetchingCompanies } =
    useFetchCompaniesQuery(1);

  const companies: ICompany[] = companiesData?.data || [];

  // ---------------- APIs ----------------

  const { data: jobFunctionsRes, isLoading: jfLoading } =
    useFetchAllJobFunctionsQuery({});

  const { data: mappingRes, isLoading: mappingLoading } =
    useFetchAllCompanyFunctionsMappingQuery(
      { company_id: companyId as number },
      { skip: !companyId }
    );

  const jobFunctions = jobFunctionsRes?.data || [];

  // ---------------- SUB MAP ----------------

  const subMap = useMemo(() => {
    const map: Record<string, any[]> = {};
    jobFunctions.forEach((jf: any) => {
      map[String(jf.id)] = jf.job_sub_functions || [];
    });
    return map;
  }, [jobFunctions]);

  // ---------------- FLATTEN ----------------

  const mappedData: RowData[] = useMemo(() => {
    const list = mappingRes?.data || [];
    const result: RowData[] = [];

    list.forEach((item: any) => {
      const jobFunctionId = item.ta_job_function_id
        ? String(item.ta_job_function_id)
        : undefined;

      const parentName = item.company_job_function_name;
      const subMappings = item.sub_job_function_mappings || [];

      if (!subMappings.length) {
        result.push({
          id: String(item.id),
          companyJobFunctionName: parentName,
          companySubJobFunctionName: "",
          jobFunctionId,
        });
        return;
      }

      subMappings.forEach((sub: any) => {
        result.push({
          id: `${item.id}-${sub.id}`,
          companyJobFunctionName: parentName,
          companySubJobFunctionName: sub.company_sub_job_function_name,
          jobFunctionId,
          subJobFunctionId: sub.ta_sub_job_function_id
            ? String(sub.ta_sub_job_function_id)
            : undefined,
        });
      });
    });

    return result;
  }, [mappingRes]);

  // ---------------- STATE ----------------

  const [data, setData] = useState<RowData[]>([]);
  const [originalData, setOriginalData] = useState<RowData[]>([]);
  const [page, setPage] = useState(1);

  useEffect(() => {
    if (mappedData.length) {
      setData(mappedData);
      setOriginalData(mappedData);
    } else if (companyId) {
      setData([]);
      setOriginalData([]);
    }
  }, [mappedData, companyId]);

  // ---------------- DIRTY ----------------

  const isDirty = useMemo(() => {
    return JSON.stringify(data) !== JSON.stringify(originalData);
  }, [data, originalData]);

  // ---------------- UNMAPPED ----------------

  const hasUnmapped = useMemo(() => {
    return data.some(row => !row.jobFunctionId || !row.subJobFunctionId);
  }, [data]);

  // ---------------- PAGINATION ----------------

  const paginatedData = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return data.slice(start, start + PAGE_SIZE);
  }, [data, page]);

  // ---------------- HANDLERS ----------------

  const handleJobChange = (id: string, jobFunctionId: string) => {
    setData(prev =>
      prev.map(row =>
        row.id === id
          ? { ...row, jobFunctionId, subJobFunctionId: undefined }
          : row
      )
    );
  };

  const handleSubChange = (id: string, subJobFunctionId: string) => {
    setData(prev =>
      prev.map(row => (row.id === id ? { ...row, subJobFunctionId } : row))
    );
  };

  const handleReset = () => {
    setData(originalData);
  };

  const handleApplyChanges = () => {
    const invalidRows = data.filter(
      row => row.jobFunctionId && !row.subJobFunctionId
    );

    if (invalidRows.length) {
      Modal.error({
        title: "Incomplete Mapping",
        content:
          "Please select Sub Job Function for all selected Job Functions before applying changes.",
      });
      return;
    }

    setOriginalData(data);
  };

  // ---------------- AI ----------------

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [aiData, setAiData] = useState<RowData[]>([]);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleAiSuggest = () => {
    const unmapped = data.filter(
      row => !row.jobFunctionId || !row.subJobFunctionId
    );

    if (!unmapped.length) {
      Modal.info({ title: "All rows already mapped" });
      return;
    }

    const firstJF = jobFunctions[0];

    const suggestions = unmapped.map(row => ({
      ...row,
      jobFunctionId: firstJF ? String(firstJF.id) : undefined,
      subJobFunctionId:
        firstJF?.job_sub_functions?.[0]?.id?.toString() || undefined,
    }));

    setAiData(suggestions);
    setIsModalOpen(true);
  };

  const handleAiChange = (id: string, field: string, value: string) => {
    setAiData(prev =>
      prev.map(row =>
        row.id === id
          ? {
              ...row,
              [field]: value,
              ...(field === "jobFunctionId"
                ? { subJobFunctionId: undefined }
                : {}),
            }
          : row
      )
    );
  };

  const applySuggestions = () => {
    setData(prev =>
      prev.map(row => {
        const updated = aiData.find(a => a.id === row.id);
        return updated ? { ...row, ...updated } : row;
      })
    );
    setIsModalOpen(false);
  };

  // ---------------- CSV ----------------

  const downloadCSV = () => {
    const csv = [
      ["Company Job", "Job Function", "Company Sub", "Sub Function"],
      ...aiData.map(r => [
        r.companyJobFunctionName,
        r.jobFunctionId,
        r.companySubJobFunctionName,
        r.subJobFunctionId,
      ]),
    ]
      .map(row => row.join(","))
      .join("\n");

    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = "ai_suggestions.csv";
    a.click();
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();

    reader.onload = event => {
      const text = event.target?.result as string;

      const rows = text.split("\n").slice(1);

      const parsed = rows
        .filter(r => r.trim())
        .map(row => {
          const [
            companyJobFunctionName,
            jobFunctionId,
            companySubJobFunctionName,
            subJobFunctionId,
          ] = row.split(",");

          return {
            id: Math.random().toString(),
            companyJobFunctionName,
            companySubJobFunctionName,
            jobFunctionId,
            subJobFunctionId,
          };
        });

      setAiData(parsed);
    };

    reader.readAsText(file);
  };

  // ---------------- COLUMNS ----------------

  const columns = [
    {
      title: "Company Job Function",
      dataIndex: "companyJobFunctionName",
    },
    {
      title: "Job Function",
      render: (_: any, record: RowData) => (
        <Select
          style={{ width: "100%" }}
          loading={jfLoading}
          value={record.jobFunctionId ?? undefined}
          onChange={value => handleJobChange(record.id, value)}
        >
          {jobFunctions.map((jf: any) => (
            <Option key={jf.id} value={String(jf.id)}>
              {jf.name}
            </Option>
          ))}
        </Select>
      ),
    },
    {
      title: "Company Sub Job Function",
      dataIndex: "companySubJobFunctionName",
    },
    {
      title: "Sub Job Function",
      render: (_: any, record: RowData) => {
        const subs = record.jobFunctionId
          ? subMap[record.jobFunctionId] || []
          : [];

        return (
          <Select
            style={{ width: "100%" }}
            loading={jfLoading}
            value={record.subJobFunctionId ?? undefined}
            disabled={!record.jobFunctionId}
            onChange={value => handleSubChange(record.id, value)}
          >
            {subs.map((s: any) => (
              <Option key={s.id} value={String(s.id)}>
                {s.name}
              </Option>
            ))}
          </Select>
        );
      },
    },
  ];

  // ---------------- MODAL COLUMNS ----------------

  const modalColumns = [
    {
      title: "Company Job Function",
      dataIndex: "companyJobFunctionName",
    },
    {
      title: "Job Function",
      render: (_: any, record: RowData) => (
        <Select
          style={{ width: "100%" }}
          value={record.jobFunctionId}
          onChange={value => handleAiChange(record.id, "jobFunctionId", value)}
        >
          {jobFunctions.map((jf: any) => (
            <Option key={jf.id} value={String(jf.id)}>
              {jf.name}
            </Option>
          ))}
        </Select>
      ),
    },
    {
      title: "Company Sub Job Function",
      dataIndex: "companySubJobFunctionName",
    },
    {
      title: "Sub Job Function",
      render: (_: any, record: RowData) => {
        const subs = record.jobFunctionId
          ? subMap[record.jobFunctionId] || []
          : [];

        return (
          <Select
            style={{ width: "100%" }}
            value={record.subJobFunctionId}
            onChange={value =>
              handleAiChange(record.id, "subJobFunctionId", value)
            }
          >
            {subs.map((s: any) => (
              <Option key={s.id} value={String(s.id)}>
                {s.name}
              </Option>
            ))}
          </Select>
        );
      },
    },
  ];

  // ---------------- COMPANY SELECTION SCREEN ----------------

  if (!companyId) {
    return (
      <div className="ApplicationSelector w-full px-12">
        <div className="flex justify-between items-center mt-6 px-4">
          <h2 className="font-bold text-xl text-themeBlue text-left">
            Select the Company
          </h2>
        </div>

        {isFetchingCompanies ? (
          <div
            style={{ height: "650px" }}
            className="flex justify-center items-center"
          >
            <LoadingOutlined className="spinner" style={{ fontSize: "80px" }} />
          </div>
        ) : (
          <main className="min-h-screen bg-background flex items-center justify-center p-4">
            <div className="w-full">
              <CompanyList
                companiesList={companies}
                onSelectCompany={setSelectedCompany}
                selectedCompany={selectedCompany}
              />

              {selectedCompany && (
                <div className="mt-8 flex gap-2 sticky bottom-2">
                  <button
                    onClick={() => {
                      setCompanyId(Number(selectedCompany));
                      setPage(1);
                    }}
                    className="flex-1 bg-themeBlue text-white px-5 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
                  >
                    {
                      companies.find(
                        (item: ICompany) => String(item.id) === selectedCompany
                      )?.name
                    }
                  </button>
                </div>
              )}
            </div>
          </main>
        )}
      </div>
    );
  }

  return (
    <>
      <input
        type="file"
        accept=".csv"
        ref={fileInputRef}
        hidden
        onChange={handleFileUpload}
      />

      <div className="appTitle text-base font-[500] mx-5">
        Company Functions Mapping
      </div>

      <div className="jobCreationBar sticky top-0 z-10 bg-white px-14 py-6 rounded-lg flex justify-between items-center my-7">
        <div className="title text-themeBlue font-[500] text-3xl">
          Company Functions Mapping
        </div>

        <div className="flex gap-2">
          {isDirty && (
            <>
              <Button
                variant="download"
                icon={false}
                onClick={handleReset}
                disabled={!isDirty}
              >
                Undo
              </Button>

              <Button
                variant="add"
                icon={false}
                onClick={handleApplyChanges}
                disabled={!isDirty}
              >
                Apply Changes
              </Button>
            </>
          )}

          <Button variant="upload">Upload CSV</Button>

          <Button
            variant="add"
            icon={false}
            onClick={handleAiSuggest}
            disabled={!hasUnmapped}
          >
            AI Suggest
          </Button>
        </div>
      </div>

      <div className="jobCreationListing bg-white px-14 py-6 rounded-lg">
        <Table
          columns={columns}
          data={paginatedData}
          pagination
          page={page}
          count={data.length}
          onChangePage={p => setPage(p)}
          isLoading={mappingLoading}
        />
      </div>

      <Modal
        title="AI Suggestions"
        visible={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        width={900}
        footer={[
          <div className="footer flex items-center gap-1 justify-end">
            <Button variant="upload" onClick={handleUploadClick}>
              Upload CSV
            </Button>
            <Button variant="download" onClick={downloadCSV}>
              Download CSV
            </Button>
            <Button variant="add" onClick={applySuggestions}>
              Apply Suggestions
            </Button>
          </div>,
        ]}
      >
        <AntTable
          rowKey="id"
          dataSource={aiData}
          columns={modalColumns}
          pagination={{ pageSize: 5 }}
        />
      </Modal>
    </>
  );
};

export default CompanyFunctionsMapTable;
